import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { BookLayoutEnum } from '~/api/book/bookApiTypes';
import { pagesConfigStore } from '~/atomic/atom/links/pagesConfigStore';
import { HomeLink } from '~/atomic/atom/links/staticLinks';
import { PaperStyle } from '~/atomic/atom/PaperStyle';
import { EmptyCatalogPage } from '~/atomic/page/catalog/EmptyCatalogPage';
import { getBooks } from '~/atomic/page/catalog/genrePage/genrePage.data';
import { genrePageSelector } from '~/atomic/page/catalog/genrePage/genrePage.selector';
import { genrePageSeoSelector } from '~/atomic/page/catalog/genrePage/genrePage.seo.selector';
import {
  SCBreadCrumb,
  SCCatalog,
  SCCatalogGrid,
  SCCatalogList,
  SCLink,
  SCPaginationWrapper,
  SCSearchWord,
} from '~/atomic/page/catalog/styled';
import { FavoriteGenresCheckbox } from '~/atomic/page/index/FavoriteGenresCheckbox/FavoriteGenresCheckbox';
import {
  getGenresFromCookieAndSave,
  saveFavoriteGenresInCookie,
} from '~/atomic/page/index/feed.data';
import { feedSelector } from '~/atomic/page/index/feed.selector';
import { PageTitleStyle, TextRegStyle } from '~/atomic/Typography';
import { BookCard } from '~/feature/book/BookCard/BookCard';
import { BookListCard } from '~/feature/book/BookCard/BookListCard';
import { AuthModalWithSubscribeBook } from '~/feature/book/SubscribeBookModal/AuthModalWithSubscribeBook';
import { CallPhoneAuthModalWithSubscribeBook } from '~/feature/book/SubscribeBookModal/CallPhoneAuthModalWithSubscribeBook';
import { LoginModalWithSubscribeBook } from '~/feature/book/SubscribeBookModal/LoginModalWithSubscribeBook';
import { RegisterModalWithSubscribeBook } from '~/feature/book/SubscribeBookModal/RegisterModalWithSubscribeBook';
import { SmsPhoneAuthModalWithSubscribeBook } from '~/feature/book/SubscribeBookModal/SmsPhoneAuthModalWithSubscribeBook';
import { CatalogBookLayout } from '~/feature/catalog/CatalogBookLayout/CatalogBookLayout';
import { catalogBookLayoutSelector } from '~/feature/catalog/CatalogBookLayout/catalogBookLayout.selector';
import { CatalogSkeleton } from '~/feature/catalog/CatalogSkeleton';
import { CatalogFilter } from '~/feature/catalog/filter/CatalogFilter';
import {
  catalogFilterFirstGenreSelector,
  catalogFilterSelector,
} from '~/feature/catalog/filter/catalogFilter.selector';
import { Pagination } from '~/feature/pagination/Pagination';
import { paginationSelector } from '~/feature/pagination/pagination.selector';
import { userSelector } from '~/feature/user/user.selector';
import { trackingShowGenreInVkMetrika } from '~/feature/vk/VkMetrikaInit';
import { environments } from '~/lib/const';
import { useAppDispatch, useAppSelector } from '~/store';

export const GenrePage = () => {
  const dispatch = useAppDispatch();

  const { isAuth } = useAppSelector(userSelector);
  const { h1 } = useAppSelector(genrePageSeoSelector);
  const firstGenre = useAppSelector(catalogFilterFirstGenreSelector);
  const { isCanDownload, withAdvertisingFromBookriver } = useAppSelector(catalogFilterSelector);
  const { genres } = useAppSelector(feedSelector);
  const { total } = useAppSelector(paginationSelector);
  const {
    books,
    isLoadBooks,
    genreInfo,
  } = useAppSelector(genrePageSelector);
  const { layout } = useAppSelector(catalogBookLayoutSelector);
  const isShowFavoriteGenres = !firstGenre?.name;

  const [isFirstLoad, setIsFirstLoad] = useState(true);

  useEffect(() => {
    if (isShowFavoriteGenres) {
      setIsFirstLoad(false);
    }
  }, [isShowFavoriteGenres]);

  const loadFavoriteGenres = async () => {
    await dispatch(getGenresFromCookieAndSave(undefined));
  };

  const loadBooks = () => {
    return dispatch(getBooks());
  };

  useEffect(() => {
    if (isAuth) {
      loadBooks();
    }
  }, [isAuth]);

  useEffect(() => {
    if (isAuth && isShowFavoriteGenres) {
      loadFavoriteGenres();
    }
  }, [isAuth, isShowFavoriteGenres]);

  useEffect(() => {
    setTimeout(() => {
      const isCurrentPage = window.location.pathname.startsWith(pagesConfigStore.genre.url());

      if ((!isFirstLoad) && isCurrentPage && isShowFavoriteGenres) {
        loadBooks();
        dispatch(saveFavoriteGenresInCookie(undefined));
      }
    }, 1);
  }, [genres]);

  useEffect(() => {
    if (environments.isClient) {
      trackingShowGenreInVkMetrika(firstGenre?.slug);
    }
  }, [firstGenre?.slug]);

  const isShowBooks = (books?.length ?? 0) > 0 && !isLoadBooks;
  const isShowEmptyCatalog = (books?.length ?? 0) < 1 && !isLoadBooks;

  return (
    <>
      <SCCatalog>
        <SCBreadCrumb>
          <HomeLink>
            <SCLink>Главная</SCLink>
          </HomeLink>
          {' / '}
          {firstGenre?.name ?? 'Все жанры'}
        </SCBreadCrumb>
        <SCSearchWord>{h1}</SCSearchWord>
        <CatalogFilter
          isShowAll
          onChange={loadBooks}
        />
        {isShowFavoriteGenres ? (
          <FavoriteGenresCheckbox />
        ) : null}
        <CatalogBookLayout
          booksTotal={total}
        />
        <CatalogSkeleton isShow={isLoadBooks} />
        {isShowEmptyCatalog && <EmptyCatalogPage />}
        {isShowBooks && (
          <>
            {layout === BookLayoutEnum.GRID && (
              <SCCatalogGrid>
                {books.map((book) => (
                  <BookCard
                    isFb2={isCanDownload}
                    key={book.id}
                    book={book}
                    bookLinkQueries={withAdvertisingFromBookriver
                      ? {
                        ...book?.advertisingFromBookriver?.VMDAdCampaign?.utmQueryParams,
                        utm_content: 'genre',
                      }
                      : undefined}
                  />
                ))}
              </SCCatalogGrid>
            )}
            {layout === BookLayoutEnum.LIST && (
              <SCCatalogList>
                {books.map((book) => (
                  <BookListCard
                    isFb2={isCanDownload}
                    key={book.id}
                    book={book}
                    bookLinkQueries={withAdvertisingFromBookriver
                      ? {
                        ...book?.advertisingFromBookriver?.VMDAdCampaign?.utmQueryParams,
                        utm_content: 'genre',
                      }
                      : undefined}
                  />
                ))}
              </SCCatalogList>
            )}
            <SCPaginationWrapper>
              <Pagination onChange={loadBooks} />
            </SCPaginationWrapper>
            {genreInfo?.description && (
              <SCGenreDescription>
                <SCSCGenreDescriptionTitle>
                  {genreInfo?.name}
                </SCSCGenreDescriptionTitle>
                <SCGenreDescriptionText dangerouslySetInnerHTML={{ __html: genreInfo?.description }} />
              </SCGenreDescription>
            )}
          </>
        )}
      </SCCatalog>
      <AuthModalWithSubscribeBook />
      <LoginModalWithSubscribeBook />
      <RegisterModalWithSubscribeBook />
      <CallPhoneAuthModalWithSubscribeBook />
      <SmsPhoneAuthModalWithSubscribeBook />
    </>
  );
};

const SCGenreDescription = styled.div`
  margin-top: 30px;
`;

const SCSCGenreDescriptionTitle = styled.h2`
  ${PageTitleStyle};
  margin-bottom: 20px;
`;

const SCGenreDescriptionText = styled.div`
  ${TextRegStyle};
  color: var(--black-color);

  ul {
    padding-left: 40px;
    margin-bottom: 20px;
    list-style: initial;
  }

  > * {
    ${TextRegStyle};
    color: var(--black-color);

    > * {
      ${TextRegStyle};
      color: var(--black-color);
    }
  }
`;

const SCRecommendationText = styled.div`
  ${PaperStyle};
  padding: 12px;
  ${TextRegStyle};
  color: var(--black-color);
  margin-bottom: 12px;
`;
